import { useOutlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// ─────────────────────────────────────────────────────────────────────────────
// AnimatedOutlet
//
// Drop-in replacement for <Outlet /> that wraps every routed page in a
// framer-motion div keyed by pathname, giving smooth fade+slide transitions
// without needing to touch individual page components.
//
// Usage: replace <Outlet /> in any layout with <AnimatedOutlet />.
// ─────────────────────────────────────────────────────────────────────────────

const variants = {
  initial: { opacity: 0,  y: 20 },
  animate: { opacity: 1,  y: 0,  transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit:    { opacity: 0,         transition: { duration: 0.15, ease: 'easeIn' } },
};

export default function AnimatedOutlet() {
  const element  = useOutlet();
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ willChange: 'opacity, transform' }}
      >
        {element}
      </motion.div>
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AdminAnimatedOutlet — lighter transition for the admin panel content area
// (no y shift, just fade — avoids jarring movement with the fixed sidebar)
// ─────────────────────────────────────────────────────────────────────────────

const adminVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.18 } },
  exit:    { opacity: 0, transition: { duration: 0.1  } },
};

export function AdminAnimatedOutlet() {
  const element  = useOutlet();
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={adminVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {element}
      </motion.div>
    </AnimatePresence>
  );
}
